import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Calendar, User, Search, Clock, Filter } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useRTLArrow } from '@/hooks/useRTL';
import { useContent } from '@/hooks/useContent';
import { useLanguage } from '@/hooks/useLanguage';

gsap.registerPlugin(ScrollTrigger);

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime?: number;
  image?: string;
}

const Blog = () => {
  const { t } = useContent();
  const { isRTL } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { getArrow } = useRTLArrow();
  const ArrowIcon = getArrow('right');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Get blog posts from content
  const allPosts = useMemo(() => {
    const posts = t('blog.posts', { returnObjects: true }) as BlogPost[];
    return Array.isArray(posts) ? posts : [];
  }, [t]);

  // Filter posts based on search and category
  const filteredPosts = useMemo(() => {
    return allPosts.filter((post) => {
      const matchesSearch = 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.author.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = 
        selectedCategory === 'all' || post.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [allPosts, searchQuery, selectedCategory]);

  // Get categories
  const categories = useMemo(() => {
    const cats = t('blog.categories', { returnObjects: true }) as Record<string, string>;
    return cats || {};
  }, [t]);

  useEffect(() => {
    document.title = `${t('blog.title')} - ${t('common.brandName')}`;
  }, [t]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryName = (categoryKey: string) => {
    return categories[categoryKey] || categoryKey;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('blog.title')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-light-secondary dark:bg-dark-secondary border-b border-light-border dark:border-dark-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-text-secondary dark:text-dark-text-secondary" size={20} />
              <input
                type="text"
                placeholder={t('blog.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary border border-light-border dark:border-dark-border text-light-text dark:text-dark-text placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-purple-base dark:focus:ring-brand-orange-base transition-all"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Filter size={20} className="text-light-text-secondary dark:text-dark-text-secondary" />
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary hidden md:inline">
                {t('blog.filterByCategory')}:
              </span>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedCategory === 'all'
                      ? 'bg-brand-purple-base dark:bg-brand-orange-base text-white'
                      : 'bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border'
                  }`}
                >
                  {t('blog.allCategories')}
                </button>
                {Object.entries(categories).map(([key, name]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedCategory === key
                        ? 'bg-brand-purple-base dark:bg-brand-orange-base text-white'
                        : 'bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary">
                {t('blog.noPostsFound')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <ScrollReveal key={post.id} delay={index * 0.1}>
                  <article className="bg-light-secondary dark:bg-dark-secondary rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border border-light-border dark:border-dark-border group">
                    {/* Image/Header */}
                    <div className="h-48 bg-gradient-to-br from-brand-purple-base via-brand-purple-light to-brand-orange-base dark:from-brand-orange-base dark:via-brand-orange-light dark:to-brand-purple-base relative overflow-hidden">
                      {post.image ? (
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-all duration-300" />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-white/90 dark:bg-dark-primary/90 text-brand-purple-base dark:text-brand-orange-base backdrop-blur-sm">
                          {getCategoryName(post.category)}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h2 className="text-2xl font-bold mb-3 text-light-text dark:text-dark-text group-hover:text-brand-purple-base dark:group-hover:text-brand-orange-base transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      {/* Meta Information */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4 pb-4 border-b border-light-border dark:border-dark-border">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-brand-purple-base dark:text-brand-orange-base" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-brand-purple-base dark:text-brand-orange-base" />
                          <span>{formatDate(post.date)}</span>
                        </div>
                        {post.readTime && (
                          <div className="flex items-center gap-2">
                            <Clock size={16} className="text-brand-purple-base dark:text-brand-orange-base" />
                            <span>{post.readTime} {t('blog.readTime')}</span>
                          </div>
                        )}
                      </div>

                      {/* Read More Link */}
                      <Link
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center gap-2 text-brand-purple-base dark:text-brand-orange-base font-semibold hover:gap-3 transition-all group/link"
                      >
                        {t('common.readMore')}
                        <ArrowIcon size={16} className="group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </article>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
